import React, { useCallback, useMemo } from 'react'
import i18n from 'simple-react-i18n'
import echarts from 'echarts/lib/echarts'
import ReactECharts from 'echarts-for-react'
import { countBy, groupBy, isFunction, keys, template } from 'lodash'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { exportExcelIcon, exportPictureIcon } from 'components/echart/EChartUtils'
import { hasValue } from 'utils/NumberUtil'
import PropTypes from 'prop-types'
import { getYear } from 'utils/DateUtil'
import CatchmentDto from 'catchment/dto/CatchmentDto'
import { getContaminationState, getNitratesState, getPesticidesState } from 'catchment/utils/CatchmentUtil'
import useApplicationSetting, { intParser, listStringParser } from 'utils/customHook/useApplicationSetting'
import CatchmentPointDto from 'catchment/dto/CatchmentPointDto'
import moment from 'moment'
import { DEFAULT_REGROUPMENT, DEFAULT_THRESHOLD_NITRATES, DEFAULT_THRESHOLD_PESTICIDES_1, DEFAULT_THRESHOLD_PESTICIDES_2, NITRATES_CODE } from 'catchment/constants/CatchmentConstant'
import DtoAnalysisUltraLight from 'quality/dto/analyse/DtoAnalysisUltraLight'
import useEndPeriod from 'catchment/utils/useEndPeriod'
import { THRESHOLD } from 'quality/constants/QualityConstants'
import { bgGrey, blackColor, disabledBorderInputColor, PASTEL_COLOR, LIGHTER_PASTEL_COLOR, favOrange, errorColor, INDICATORS_COLORS } from 'utils/constants/ColorTheme'
import useBoolean from 'utils/customHook/useBoolean'
import { Chip, Dialog, DialogContent, Grid } from '@mui/material'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import { useState } from 'react'
import { CardTable } from 'components/datatable/NewTable'
import { push } from '@lagunovsky/redux-react-router'
import { exportFile } from 'utils/ExportDataUtil'
import TooltipAnalyse from 'quality/components/qualityComponents/TooltipAnalyse'
import { THRESHOLD_COLOR, THRESHOLD_DISCRIMINANT, THRESHOLD_DISPLAY, THRESHOLD_TYPE } from 'utils/QualityThresholdUtils'
import { SHOW_INSEE_CODE } from 'account/constants/AccountSettingsConstants'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import { getI18nOrLabel } from 'utils/StringUtil'
import { getLabel } from 'utils/StoreUtils'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import { STATION_TYPE_CONSTANT, STATION_TYPE_NAME } from 'station/constants/StationConstants'

const EXPORT_HEADERS = ['contamination', 'bookmark', 'havePriority', 'sensitive', 'department', 'code', 'catchment', 'mainCatchmentPoint', 'nbPoint', 'grenelle', 'buildingOwner', 'aac']

const PriorityChip = ({
    title,
    color = 'default',
}) => {
    return (
        <Chip
            label={title}
            size={'small'}
            color={color}
            sx={{ mr: 1, maxWidth: '16px', maxHeight: '16px', minHeight: '16px', minWidth: '16px', fontSize: '8px',
                '& .MuiChip-label': {
                    padding: 0,
                },
            }}

        />
    )
}

PriorityChip.propTypes = {
    title: PropTypes.string,
    color: PropTypes.string,
}

const createSeries = (key = '', group = [], color = '', opacColor = '', name = '', referenceSdages = []) => {
    const {
        reference,
        other,
    } = countBy(group, c => referenceSdages.includes(c.usedSdage) ? 'reference' : 'other')
    return [{
        type: 'bar',
        label: {
            show: !!other,
            position: 'inside',
            color: blackColor,
        },
        data: [{
            value: [other, key],
            name: 'other',
        }],
        name,
        stack: true,
        barMaxWidth: 40,
        itemStyle: {
            color,
        },
    }, {
        type: 'bar',
        label: {
            show: !!reference,
            position: 'inside',
            color: blackColor,
        },
        data: [{
            value: [reference, key],
            name: 'reference',
        }],
        name,
        stack: true,
        barMaxWidth: 40,
        itemStyle: {
            color: opacColor,
        },
    }]
}

const createOptions = (title = '', series = [], yData = [], legendData = [], exportAction = () => {}) => ({
    title: {
        text: title,
        left: 'center',
    },
    series,
    xAxis: [{
        type: 'value',
        name: i18n.nbCatchment,
        nameLocation: 'middle',
        nameGap: 25,
    }],
    yAxis: [{
        type: 'category',
        data: yData,
        axisLabel: {
            formatter: name => i18n[name],
        },
    }],
    legend: {
        data: legendData,
        top: '10%',
        left: '10%',
        right: '20%',
        type: 'scroll',
    },
    grid: {
        top: '18%',
        left: '6%',
        right: '3%',
        bottom: '80',
        containLabel: true,
        height: 200,
    },
    tooltip: {
        trigger: 'axis',
        axisPointer: {
            type: 'shadow',
            snap: true,
        },
        formatter: params => {
            const sdageResult = params.find(p => p.data.name === 'reference').data.value[0] || 0
            const sdageTooltip = sdageResult ? `${i18n.referenceSDAGE}: ${sdageResult} ${sdageResult > 1 ? i18n.catchments : i18n.catchment}</br>` : ''
            const otherResult = params.find(p => p.data.name === 'other').data.value[0] || 0
            if (sdageTooltip) {
                return `${sdageTooltip}${i18n.otherCatchment}: ${otherResult} ${otherResult > 1 ? i18n.catchments : i18n.catchment}`
            }
            return `${params[0].seriesName}: ${otherResult} ${otherResult > 1 ? i18n.catchments : i18n.catchment}`
        },
    },
    toolbox: {
        right: '3%',
        top: '5%',
        feature: {
            saveAsImage: {
                title: i18n.export,
                icon: exportPictureIcon,
                name: title,
            },
            myToolExport: isFunction(exportAction) ? {
                title: i18n.excelExport,
                icon: exportExcelIcon,
                onclick: () => exportAction(),
            } : {},
        },
    },
})

const TooltipNitrates = ({
    analyse = {},
    threshold1,
    showCode = true,
}) => (
    <TooltipAnalyse
        title={`${i18n.nitrates} ${showCode ? `[${NITRATES_CODE}]` : ''}`}
        analyse={analyse}
        threshold={{
            parameterCode: analyse.parameter,
            threshold1,
            thresholdType: THRESHOLD_TYPE.INCREASING,
            display: THRESHOLD_DISPLAY.INCREASING,
            discriminant: THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL,
        }}
        overrideColors={[THRESHOLD_COLOR.green, THRESHOLD_COLOR.red]}
    />
)

TooltipNitrates.propTypes = {
    analyse: PropTypes.instanceOf(DtoAnalysisUltraLight),
    threshold1: PropTypes.number,
    showCode: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
}

const ContaminationDialog = ({
    popinData = [],
    popinRows = [],
    isPopinOpen = false,
    closePopin = () => {},
    headers = [],
    exportHeaders = [],
    lineActions,
}) => {
    const dispatch = useDispatch()

    const dialogTitle = useMemo(() => {
        if (popinData?.value?.[1] === 'pesticides') return template(i18n.contaminationBy)({ molecule: i18n.pesticides })
        if (popinData?.value?.[1] === 'nitrates') return template(i18n.contaminationBy)({ molecule: i18n.nitrates })
        if (popinData?.value?.[1] === 'nitratesAndPesticides') return template(i18n.contaminationBy)({ molecule: i18n.nitratesAndPesticides })
        return getI18nOrLabel(popinData.value?.[1])
    }, [popinData])

    const exportAction = useMemo(() =>({
        icon: 'file_download',
        tooltip: i18n.export,
        onClick: () => {
            const data = popinRows.length ? [{
                ...popinRows[0],
                headers: exportHeaders,
            }, ...popinRows.slice(1)] : []
            exportFile({
                data,
                exportType: 'xlsx',
                titleFile: dialogTitle,
            })
        },
    }), [popinRows, dialogTitle, exportHeaders])

    return (
        <Dialog
            maxWidth='lg'
            fullWidth
            open={isPopinOpen}
        >
            <DefaultDialogTitle
                title={dialogTitle}
                onClose={closePopin}
            />
            <DialogContent>
                <Grid container rowSpacing={0.5} columnSpacing={1}>
                    <Grid size={12}>
                        <CardTable
                            rows={popinRows}
                            headers={headers}
                            onClickRow={s => dispatch(push(`/station/catchment/${s.id}`))}
                            actions={[exportAction]}
                            lineActions={lineActions}
                            hideNbElements
                        />
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    )
}

ContaminationDialog.propTypes = {
    popinData: PropTypes.shape({
        value: PropTypes.arrayOf([PropTypes.number, PropTypes.string]),
        name: PropTypes.string,
    }),
    popinRows: PropTypes.arrayOf(PropTypes.shape({})),
    dialogTitle: PropTypes.string,
    isPopinOpen: PropTypes.bool,
    closePopin: PropTypes.func,
    headers: PropTypes.arrayOf(PropTypes.oneOfType(
        [PropTypes.string, PropTypes.shape({})],
    )),
    exportHeaders: PropTypes.arrayOf(PropTypes.string),
    lineActions: PropTypes.arrayOf(PropTypes.shape({
        icon: PropTypes.func,
    })),
}

const lineActions = [
    { icon: (row) => row.bookmark?.value === i18n.yes ? <StarRoundedIcon sx={{ color: favOrange }} /> : undefined },
    { icon: (row) => row.havePriority?.value === i18n.yes ? <PriorityChip title={'P'} color='error'/> : undefined },
    { icon: (row) => row.sensitive?.value === i18n.yes ? <PriorityChip title={'S'} color='warning'/> : undefined },
]

const ContaminationNitratesStatusChart = ({
    catchments = [],
    catchmentPoints = [],
    analysis = [],
    useP90 = false,
    referenceSdages = [],
}) => {
    const {
        qualitometers,
        departments,
        contributors,
        userBookmarks,
        allCatchmentPoints,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
        departments: store.CityReducer.departments,
        contributors: store.ContributorReducer.contributors,
        userBookmarks: store.UserReducer.userBookmarks,
        allCatchmentPoints: store.CatchmentReducer.catchmentPoints,
    }), shallowEqual)

    const {
        value: isPopinOpen,
        setTrue: openPopin,
        setFalse: closePopin,
    } = useBoolean(false)
    const [popinData, setPopinData] = useState({ value: [-1, ''], name: '' })

    const thresholdNitrates = useApplicationSetting('CATCHMENT_NITRATES_THRESHOLD', setting => setting ? parseFloat(setting) : DEFAULT_THRESHOLD_NITRATES)
    const endPeriod = useEndPeriod(intParser)
    const regroupment = useApplicationSetting('CATCHMENT_REGROUPING', setting => setting ? parseInt(setting) : DEFAULT_REGROUPMENT)
    const calculationMethod = useApplicationSetting('CATCHMENT_OVERVIEW_CALCULATION_METHOD', listStringParser)

    const generalShowInseeCode = useApplicationSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : true)
    const showInseeCode = useAccountSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : generalShowInseeCode)

    const optionsCalculState = useMemo(() => ({
        thresholdNitrates,
        useP90,
        calculationMethod,
    }), [thresholdNitrates, useP90, calculationMethod])

    const currentYear = useMemo(() => moment(endPeriod, 'YYYY').endOf('year').valueOf(), [endPeriod])
    const lastYear = useMemo(() => moment(endPeriod, 'YYYY').subtract(regroupment - 1, 'years').startOf('year').valueOf(), [endPeriod, regroupment])

    const nitratesAnalysisGroup = useMemo(() => {
        const nitratesAnalysis = analysis.filter(({ parameter }) => parameter === NITRATES_CODE)
        return groupBy(nitratesAnalysis, STATION_TYPE_NAME.qualitometer)
    }, [analysis])

    const groupCatchment = useMemo(() => groupBy(catchments, c => {
        const points = catchmentPoints.filter(p => p.id === c.id)
        const ids = points.map(p => qualitometers.find(q => q.code === p.codeWithoutDesignation)?.id).filter(id => hasValue(id))

        const nitrates = ids.flatMap(id => nitratesAnalysisGroup[id] || [])
        return getNitratesState(nitrates, optionsCalculState)
    }), [catchments, catchmentPoints, qualitometers, nitratesAnalysisGroup, optionsCalculState])

    const series = useMemo(() => ([
        ...createSeries('missingData', groupCatchment.missingData, disabledBorderInputColor, bgGrey, i18n.missingData, referenceSdages),
        ...createSeries('nitrates', groupCatchment.nitrates, PASTEL_COLOR.yellow, LIGHTER_PASTEL_COLOR.yellow, i18n.nitrates, referenceSdages),
        ...createSeries('noContaminationHigherThanThreshold', groupCatchment.noContaminationHigherThanThreshold, PASTEL_COLOR.blue, LIGHTER_PASTEL_COLOR.blue, i18n.noContaminationHigherThanThreshold, referenceSdages),
    ]), [groupCatchment, referenceSdages])

    const catchmentBookmarks = useMemo(() => {
        return userBookmarks.filter(b => b.stationType === STATION_TYPE_CONSTANT.CATCHMENT).map(b => b.identifiant)
    }, [userBookmarks])

    const exportDataFormated = catchments.map(catchment => {
        const points = allCatchmentPoints.filter(p => p.id === catchment.id)
        const mainPoint = points.find(p => p.mainPoint)
        const buildingOwner = mainPoint?.owner || (!!points.length && points.every(p => p.owner === points[0].owner) && points[0].owner)
        const isFav = catchmentBookmarks.some(code => code === catchment.code)
        return {
            contamination: groupCatchment ? getI18nOrLabel(keys(groupCatchment)?.find(key => groupCatchment[key]?.some(c => catchment.id === c.id))) : '',
            bookmark: { value: isFav ? i18n.yes : i18n.no },
            havePriority: { value: catchment.priority ? i18n.yes : i18n.no },
            sensitive: { value: catchment.sensitive ? i18n.yes : i18n.no },
            department: { value: getLabel(departments, catchment.department, showInseeCode ? 'labelWithCode' : 'name') },
            departmentCode: catchment.department,
            code: { value: catchment.code, cellType: 'left' },
            catchment: { value: catchment.name },
            mainCatchmentPoint: { value: mainPoint?.code, cellType: 'left' },
            nbPoint: { value: points.length, cellType: 'left' },
            grenelle: { value: catchment.grenelleWork ? i18n.yes : i18n.no },
            buildingOwner: { value: buildingOwner ? getLabel(contributors, buildingOwner, 'labelDisplay', 'id') : '' },
            aac: { value: catchment.aacNum },
            id: catchment.id,
        }
    })

    const popinRows = useMemo(() => {
        const contaminationTarget = popinData.value?.[1]
        const popinCatchments = groupCatchment[contaminationTarget]
        return popinCatchments?.map(c => exportDataFormated.find(catchment => catchment.id === c.id))
    }, [popinData, groupCatchment, exportDataFormated])

    const exportAction = useCallback(() => {
        const catchmentsContamination = catchments.map(c => exportDataFormated.find(catchment => c.id === catchment.id))
        const exportData = catchmentsContamination.length ? [{ ...catchmentsContamination[0], headers: EXPORT_HEADERS }, ...catchmentsContamination.slice(1)] : []
        exportFile({
            data: exportData,
            exportType: 'xlsx',
            titleFile: `${template(i18n.stateContamination)({ molecule: i18n.nitrates })}`,
        })
    }, [catchments, exportDataFormated])

    const title = useMemo(() => {
        const startYear = getYear(lastYear)
        const endYear = getYear(currentYear)
        if (startYear === endYear) {
            return `${template(i18n.stateContamination)({ molecule: i18n.nitrates })} - ${i18n.year} ${startYear}`
        }
        return `${template(i18n.stateContamination)({ molecule: i18n.nitrates })} - ${i18n.periode} ${startYear}-${endYear}`
    }, [currentYear, lastYear])

    const options = useMemo(() => createOptions(
        title,
        series,
        ['noContaminationHigherThanThreshold', 'nitrates', 'missingData'],
        [{ name: i18n.missingData }, { name: i18n.nitrates }, { name: i18n.noContaminationHigherThanThreshold }],
        exportAction,
    ), [title, series, exportAction])

    return (
        <>
            <ReactECharts
                echarts={echarts}
                option={options}
                notMerge={true}
                lazyUpdate={true}
                style={{ height: 300, paddingTop: 10 }}
                onEvents={
                    {
                        click: (params) => {
                            setPopinData(params.data)
                            openPopin()
                        },
                    }
                }
            />
            <ContaminationDialog
                popinData={popinData}
                popinRows={popinRows}
                isPopinOpen={isPopinOpen}
                closePopin={closePopin}
                headers={['department', 'code', 'catchment', 'mainCatchmentPoint', 'nbPoint', 'grenelle', 'buildingOwner', 'aac']}
                exportHeaders={EXPORT_HEADERS}
                lineActions={lineActions}
            />
        </>
    )
}

ContaminationNitratesStatusChart.propTypes = {
    catchments: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentDto)),
    catchmentPoints: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentPointDto)),
    analysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisUltraLight)),
    useP90: PropTypes.bool,
    referenceSdages: PropTypes.arrayOf(PropTypes.number),
}

const ContaminationPesticidesStatusChart = ({
    catchments = [],
    catchmentPoints = [],
    analysis = [],
    useP90 = false,
    referenceSdages = [],
}) => {
    const {
        qualityThresholds,
        qualitometers,
        departments,
        contributors,
        userBookmarks,
        allCatchmentPoints,
    } = useSelector(store => ({
        qualityThresholds: store.QualityReducer.qualityThresholds,
        qualitometers: store.QualityReducer.qualitometersLight,
        departments: store.CityReducer.departments,
        contributors: store.ContributorReducer.contributors,
        userBookmarks: store.UserReducer.userBookmarks,
        allCatchmentPoints: store.CatchmentReducer.catchmentPoints,
    }), shallowEqual)

    const {
        value: isPopinOpen,
        setTrue: openPopin,
        setFalse: closePopin,
    } = useBoolean(false)
    const [popinData, setPopinData] = useState({ value: [-1, ''], name: '' })

    const thresholdPesticides1 = useApplicationSetting('CATCHMENT_PESTICIDE_THRESHOLD_1', setting => setting ? parseFloat(setting) : DEFAULT_THRESHOLD_PESTICIDES_1)
    const thresholdPesticides2 = useApplicationSetting('CATCHMENT_PESTICIDE_THRESHOLD_2', setting => setting ? parseFloat(setting) : DEFAULT_THRESHOLD_PESTICIDES_2)
    const specificThreshold = useApplicationSetting('CATCHMENT_SPECIFIC_THRESHOLD')

    const listSumPesticides = useApplicationSetting('CATCHMENT_LIST_SUM_PESTICIDES', listStringParser)

    const threshold = qualityThresholds.find(t => t.thresholdCode === specificThreshold && t.thresholdType === THRESHOLD.PC)

    const endPeriod = useEndPeriod(intParser)

    const regroupment = useApplicationSetting('CATCHMENT_REGROUPING', setting => setting ? parseInt(setting) : DEFAULT_REGROUPMENT)
    const calculationMethod = useApplicationSetting('CATCHMENT_OVERVIEW_CALCULATION_METHOD', listStringParser)

    const generalShowInseeCode = useApplicationSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : true)
    const showInseeCode = useAccountSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : generalShowInseeCode)

    const optionsCalculState = useMemo(() => ({
        thresholdPesticides1,
        thresholdPesticides2,
        useP90,
        threshold,
        listSumPesticides,
        calculationMethod,
    }), [thresholdPesticides1, thresholdPesticides2, useP90, threshold, listSumPesticides, calculationMethod])

    const currentYear = useMemo(() => moment(endPeriod, 'YYYY').endOf('year').valueOf(), [endPeriod])
    const lastYear = useMemo(() => moment(endPeriod, 'YYYY').subtract(regroupment - 1, 'years').startOf('year').valueOf(), [endPeriod, regroupment])

    const pesticidesAnalysisGroup = useMemo(() => {
        const pesticidesAnalysis = analysis.filter(({ parameter }) => parameter !== NITRATES_CODE)
        return groupBy(pesticidesAnalysis, STATION_TYPE_NAME.qualitometer)
    }, [analysis])

    const groupCatchmentPesticides = useMemo(() => groupBy(catchments, c => {
        const points = catchmentPoints.filter(p => p.id === c.id)
        const ids = points.map(p => qualitometers.find(q => q.code === p.codeWithoutDesignation)?.id).filter(id => hasValue(id))

        const pesticides = ids.flatMap(id => pesticidesAnalysisGroup[id] || [])
        return getPesticidesState(pesticides, optionsCalculState)
    }), [catchments, catchmentPoints, qualitometers, pesticidesAnalysisGroup, optionsCalculState])

    const series = useMemo(() => ([
        ...createSeries('missingData', groupCatchmentPesticides.missingData, disabledBorderInputColor, bgGrey, i18n.missingData, referenceSdages),
        ...createSeries('pesticides', groupCatchmentPesticides.pesticides, PASTEL_COLOR.orange, LIGHTER_PASTEL_COLOR.orange, i18n.pesticides, referenceSdages),
        ...createSeries('noContaminationHigherThanThreshold', groupCatchmentPesticides.noContaminationHigherThanThreshold, PASTEL_COLOR.blue, LIGHTER_PASTEL_COLOR.blue, i18n.noContaminationHigherThanThreshold, referenceSdages),
    ]), [groupCatchmentPesticides, referenceSdages])

    const catchmentBookmarks = useMemo(() => {
        return userBookmarks.filter(b => b.stationType === STATION_TYPE_CONSTANT.CATCHMENT).map(b => b.identifiant)
    }, [userBookmarks])

    const exportDataFormated = catchments.map(catchment => {
        const points = allCatchmentPoints.filter(p => p.id === catchment.id)
        const mainPoint = points.find(p => p.mainPoint)
        const buildingOwner = mainPoint?.owner || (!!points.length && points.every(p => p.owner === points[0].owner) && points[0].owner)
        const isFav = catchmentBookmarks.some(code => code === catchment.code)
        return {
            contamination: groupCatchmentPesticides ? getI18nOrLabel(keys(groupCatchmentPesticides)?.find(key => groupCatchmentPesticides[key]?.some(c => catchment.id === c.id))) : '',
            bookmark: { value: isFav ? i18n.yes : i18n.no },
            havePriority: { value: catchment.priority ? i18n.yes : i18n.no },
            sensitive: { value: catchment.sensitive ? i18n.yes : i18n.no },
            department: { value: getLabel(departments, catchment.department, showInseeCode ? 'labelWithCode' : 'name') },
            departmentCode: catchment.department,
            code: { value: catchment.code, cellType: 'left' },
            catchment: { value: catchment.name },
            mainCatchmentPoint: { value: mainPoint?.code, cellType: 'left' },
            nbPoint: { value: points.length, cellType: 'left' },
            grenelle: { value: catchment.grenelleWork ? i18n.yes : i18n.no },
            buildingOwner: { value: buildingOwner ? getLabel(contributors, buildingOwner, 'labelDisplay', 'id') : '' },
            aac: { value: catchment.aacNum },
            id: catchment.id,
        }
    })

    const popinRows = useMemo(() => {
        const contaminationTarget = popinData.value?.[1]
        const popinCatchments = groupCatchmentPesticides[contaminationTarget]
        return popinCatchments?.map(c => exportDataFormated.find(catchment => catchment.id === c.id))
    }, [popinData, groupCatchmentPesticides, exportDataFormated])

    const exportAction = useCallback(() => {
        const catchmentsContamination = catchments.map(c => exportDataFormated.find(catchment => c.id === catchment.id))
        const exportData = catchmentsContamination.length ? [{ ...catchmentsContamination[0], headers: EXPORT_HEADERS }, ...catchmentsContamination.slice(1)] : []
        exportFile({
            data: exportData,
            exportType: 'xlsx',
            titleFile: `${template(i18n.stateContamination)({ molecule: i18n.pesticides })}`,
        })
    }, [catchments, exportDataFormated])

    const title = useMemo(() => {
        const startYear = getYear(lastYear)
        const endYear = getYear(currentYear)
        if (startYear === endYear) {
            return `${template(i18n.stateContamination)({ molecule: i18n.pesticides })} - ${i18n.year} ${startYear}`
        }
        return `${template(i18n.stateContamination)({ molecule: i18n.pesticides })} - ${i18n.periode} ${startYear}-${endYear}`
    }, [currentYear, lastYear])

    const options = useMemo(() => createOptions(
        title,
        series,
        ['noContaminationHigherThanThreshold', 'pesticides', 'missingData'],
        [{ name: i18n.missingData }, { name: i18n.pesticides }, { name: i18n.noContaminationHigherThanThreshold }],
        exportAction,
    ), [title, series, exportAction])

    return (
        <>
            <ReactECharts
                echarts={echarts}
                option={options}
                notMerge={true}
                lazyUpdate={true}
                style={{ height: 300, paddingTop: 10 }}
                onEvents={
                    {
                        click: (params) => {
                            setPopinData(params.data)
                            openPopin()
                        },
                    }
                }
            />
            <ContaminationDialog
                popinData={popinData}
                popinRows={popinRows}
                isPopinOpen={isPopinOpen}
                closePopin={closePopin}
                headers={['department', 'code', 'catchment', 'mainCatchmentPoint', 'nbPoint', 'grenelle', 'buildingOwner', 'aac']}
                exportHeaders={EXPORT_HEADERS}
                lineActions={lineActions}
            />
        </>
    )
}

ContaminationPesticidesStatusChart.propTypes = {
    catchments: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentDto)),
    catchmentPoints: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentPointDto)),
    analysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisUltraLight)),
    useP90: PropTypes.bool,
    referenceSdages: PropTypes.arrayOf(PropTypes.number),
}

const ContaminationStatusChart = ({
    catchments = [],
    catchmentPoints = [],
    analysis = [],
    useP90 = false,
    referenceSdages = [],
}) => {
    const {
        qualityThresholds,
        qualitometers,
        departments,
        contributors,
        userBookmarks,
        allCatchmentPoints,
    } = useSelector(store => ({
        qualityThresholds: store.QualityReducer.qualityThresholds,
        qualitometers: store.QualityReducer.qualitometersLight,
        departments: store.CityReducer.departments,
        contributors: store.ContributorReducer.contributors,
        userBookmarks: store.UserReducer.userBookmarks,
        allCatchmentPoints: store.CatchmentReducer.catchmentPoints,
    }), shallowEqual)

    const {
        value: isPopinOpen,
        setTrue: openPopin,
        setFalse: closePopin,
    } = useBoolean(false)
    const [popinData, setPopinData] = useState({ value: [-1, ''], name: '' })

    const thresholdNitrates = useApplicationSetting('CATCHMENT_NITRATES_THRESHOLD', setting => setting ? parseFloat(setting) : DEFAULT_THRESHOLD_NITRATES)
    const thresholdPesticides1 = useApplicationSetting('CATCHMENT_PESTICIDE_THRESHOLD_1', setting => setting ? parseFloat(setting) : DEFAULT_THRESHOLD_PESTICIDES_1)
    const thresholdPesticides2 = useApplicationSetting('CATCHMENT_PESTICIDE_THRESHOLD_2', setting => setting ? parseFloat(setting) : DEFAULT_THRESHOLD_PESTICIDES_2)
    const specificThreshold = useApplicationSetting('CATCHMENT_SPECIFIC_THRESHOLD')

    const listSumPesticides = useApplicationSetting('CATCHMENT_LIST_SUM_PESTICIDES', listStringParser)

    const threshold = qualityThresholds.find(t => t.thresholdCode === specificThreshold && t.thresholdType === THRESHOLD.PC)

    const endPeriod = useEndPeriod(intParser)

    const regroupment = useApplicationSetting('CATCHMENT_REGROUPING', setting => setting ? parseInt(setting) : DEFAULT_REGROUPMENT)
    const calculationMethod = useApplicationSetting('CATCHMENT_OVERVIEW_CALCULATION_METHOD', listStringParser)

    const generalShowInseeCode = useApplicationSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : true)
    const showInseeCode = useAccountSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : generalShowInseeCode)

    const optionsCalculState = useMemo(() => ({
        thresholdNitrates,
        thresholdPesticides1,
        thresholdPesticides2,
        useP90,
        threshold,
        listSumPesticides,
        calculationMethod,
    }), [thresholdNitrates, thresholdPesticides1, thresholdPesticides2, useP90, threshold, listSumPesticides, calculationMethod])

    const currentYear = useMemo(() => moment(endPeriod, 'YYYY').endOf('year').valueOf(), [endPeriod])
    const lastYear = useMemo(() => moment(endPeriod, 'YYYY').subtract(regroupment - 1, 'years').startOf('year').valueOf(), [endPeriod, regroupment])

    const nitratesAnalysisGroup = useMemo(() => {
        const nitratesAnalysis = analysis.filter(({ parameter }) => parameter === NITRATES_CODE)
        return groupBy(nitratesAnalysis, STATION_TYPE_NAME.qualitometer)
    }, [analysis])

    const pesticidesAnalysisGroup = useMemo(() => {
        const pesticidesAnalysis = analysis.filter(({ parameter }) => parameter !== NITRATES_CODE)
        return groupBy(pesticidesAnalysis, STATION_TYPE_NAME.qualitometer)
    }, [analysis])

    const groupCatchment = useMemo(() => groupBy(catchments, c => {
        const points = catchmentPoints.filter(p => p.id === c.id)
        const ids = points.map(p => qualitometers.find(q => q.code === p.codeWithoutDesignation)?.id).filter(id => hasValue(id))

        const nitrates = ids.flatMap(id => nitratesAnalysisGroup[id] || [])
        const pesticides = ids.flatMap(id => pesticidesAnalysisGroup[id] || [])
        return getContaminationState(nitrates, pesticides, optionsCalculState)
    }), [catchments, catchmentPoints, qualitometers, nitratesAnalysisGroup, pesticidesAnalysisGroup, optionsCalculState])

    const series = useMemo(() => ([
        ...createSeries('missingData', groupCatchment.missingData, disabledBorderInputColor, bgGrey, i18n.missingData, referenceSdages),
        ...createSeries('nitrates', groupCatchment.nitrates, PASTEL_COLOR.yellow, LIGHTER_PASTEL_COLOR.yellow, i18n.nitrates, referenceSdages),
        ...createSeries('pesticides', groupCatchment.pesticides, PASTEL_COLOR.orange, LIGHTER_PASTEL_COLOR.orange, i18n.pesticides, referenceSdages),
        ...createSeries('noContaminationHigherThanThreshold', groupCatchment.noContaminationHigherThanThreshold, PASTEL_COLOR.blue, LIGHTER_PASTEL_COLOR.blue, i18n.noContaminationHigherThanThreshold, referenceSdages),
        ...createSeries('nitratesAndPesticides', groupCatchment.nitratesAndPesticides, errorColor, INDICATORS_COLORS.RED, i18n.nitratesAndPesticides, referenceSdages),
    ]), [groupCatchment, referenceSdages])

    const catchmentBookmarks = useMemo(() => {
        return userBookmarks.filter(b => b.stationType === STATION_TYPE_CONSTANT.CATCHMENT).map(b => b.identifiant)
    }, [userBookmarks])

    const exportDataFormated = useMemo(() => catchments.map(catchment => {
        const points = allCatchmentPoints.filter(p => p.id === catchment.id)
        const mainPoint = points.find(p => p.mainPoint)
        const buildingOwner = mainPoint?.owner || (!!points.length && points.every(p => p.owner === points[0].owner) && points[0].owner)
        const isFav = catchmentBookmarks.some(code => code === catchment.code)
        return {
            contamination: groupCatchment ? getI18nOrLabel(keys(groupCatchment)?.find(key => groupCatchment[key]?.some(c => catchment.id === c.id))) : '',
            bookmark: { value: isFav ? i18n.yes : i18n.no },
            havePriority: { value: catchment.priority ? i18n.yes : i18n.no },
            sensitive: { value: catchment.sensitive ? i18n.yes : i18n.no },
            department: { value: getLabel(departments, catchment.department, showInseeCode ? 'labelWithCode' : 'name') },
            departmentCode: catchment.department,
            code: { value: catchment.code, cellType: 'left' },
            catchment: { value: catchment.name },
            mainCatchmentPoint: { value: mainPoint?.code, cellType: 'left' },
            nbPoint: { value: points.length, cellType: 'left' },
            grenelle: { value: catchment.grenelleWork ? i18n.yes : i18n.no },
            buildingOwner: { value: buildingOwner ? getLabel(contributors, buildingOwner, 'labelDisplay', 'id') : '' },
            aac: { value: catchment.aacNum },
            id: catchment.id,
        }
    }), [catchments, allCatchmentPoints, catchmentBookmarks, departments, showInseeCode, groupCatchment, contributors])

    const popinRows = useMemo(() => {
        const contaminationTarget = popinData.value?.[1]
        const popinCatchments = groupCatchment[contaminationTarget]
        return popinCatchments?.map(c => exportDataFormated.find(catchment => catchment.id === c.id))
    }, [popinData, groupCatchment, exportDataFormated])

    const exportAction = useCallback(() => {
        const catchmentsContamination = catchments.map(c => exportDataFormated.find(catchment => c.id === catchment.id))
        const exportData = catchmentsContamination.length ? [{ ...catchmentsContamination[0], headers: EXPORT_HEADERS }, ...catchmentsContamination.slice(1)] : []
        exportFile({
            data: exportData,
            exportType: 'xlsx',
            titleFile: `${i18n.stateContaminationNitratesPesticides}`,
        })
    }, [catchments, exportDataFormated])

    const title = useMemo(() => {
        const startYear = getYear(lastYear)
        const endYear = getYear(currentYear)
        if (startYear === endYear) {
            return `${i18n.stateContaminationNitratesPesticides} - ${i18n.year} ${startYear}`
        }
        return `${i18n.stateContaminationNitratesPesticides} - ${i18n.periode} ${startYear}-${endYear}`
    }, [currentYear, lastYear])

    const options = useMemo(() => createOptions(
        title,
        series,
        ['noContaminationHigherThanThreshold', 'nitrates', 'pesticides', 'nitratesAndPesticides', 'missingData'],
        [{ name: i18n.missingData }, { name: i18n.nitratesAndPesticides }, { name: i18n.pesticides }, { name: i18n.nitrates }, { name: i18n.noContaminationHigherThanThreshold }],
        exportAction,
    ), [title, series, exportAction])

    return (
        <>
            <ReactECharts
                echarts={echarts}
                option={options}
                notMerge={true}
                lazyUpdate={true}
                style={{ height: 300 }}
                onEvents={
                    {
                        click: (params) => {
                            setPopinData(params.data)
                            openPopin()
                        },
                    }
                }
            />
            <ContaminationDialog
                popinData={popinData}
                popinRows={popinRows}
                isPopinOpen={isPopinOpen}
                closePopin={closePopin}
                headers={['department', 'code', 'catchment', 'mainCatchmentPoint', 'nbPoint', 'grenelle', 'buildingOwner', 'aac']}
                exportHeaders={EXPORT_HEADERS}
                lineActions={lineActions}
            />
        </>
    )
}

ContaminationStatusChart.propTypes = {
    catchments: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentDto)),
    catchmentPoints: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentPointDto)),
    analysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisUltraLight)),
    useP90: PropTypes.bool,
    referenceSdages: PropTypes.arrayOf(PropTypes.number),
}

export { ContaminationNitratesStatusChart, ContaminationPesticidesStatusChart, ContaminationStatusChart }